import { useState } from 'react';
import { stanNotify } from '@/lib';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { useUser } from '@/queries/useUser';
import { useModal } from '@/hooks/useModal';
import { useDeleteAutomation, usePublishAutomation, useUnpublishAutomation } from '../queries';
import { useAutomations } from '../queries/useAutomationQueries';

interface UseAutomationActionsOptions {
    onAutomationSaved?: () => void;
}

export function useAutomationActions(options?: UseAutomationActionsOptions) {
    const { user } = useUser();
    const { data: automations = [] } = useAutomations();

    const { isOpen: isDeleteModalOpen, open: openDeleteModal, close: closeDeleteModal } = useModal();
    const [deleteAutomationId, setDeleteAutomationId] = useState<number | null>(null);

    // Use mutations from queries folder
    const publishAutomationMutation = usePublishAutomation();
    const unpublishAutomationMutation = useUnpublishAutomation();
    const deleteAutomationMutation = useDeleteAutomation();

    const handlePublishAutomation = (automationId: number) => {
        const automation = automations.find(a => a.automation_id === automationId);
        if (!automation) {
            stanNotify({
                type: 'error',
                title: 'Automation not found',
                text: 'Please refresh the page and try again.',
            });
            return;
        }

        publishAutomationMutation.mutate(
            { id: automationId, automation },
            {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Published!',
                        text: 'Auto-DM is now active.',
                    });
                    stanAnalytics('automation-published', {
                        meta: { user_id: user?.user_id, username: user?.username },
                        props: { automation_id: automationId },
                    });
                    options?.onAutomationSaved?.();
                },
                onError: () => {
                    stanNotify({
                        type: 'error',
                        title: 'Something went wrong',
                        text: 'Please contact us at friends@stanwith.me',
                    });
                },
            }
        );
    };

    const handleUnpublishAutomation = (automationId: number) => {
        const automation = automations.find(a => a.automation_id === automationId);
        if (!automation) {
            stanNotify({
                type: 'error',
                title: 'Automation not found',
                text: 'Please refresh the page and try again.',
            });
            return;
        }

        unpublishAutomationMutation.mutate(
            { id: automationId, automation },
            {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Unpublished',
                        text: 'Your auto-dm is now a draft.',
                    });
                    stanAnalytics('automation-unpublished', {
                        meta: { user_id: user?.user_id, username: user?.username },
                        props: { automation_id: automationId },
                    });
                    options?.onAutomationSaved?.();
                },
                onError: () => {
                    stanNotify({
                        type: 'error',
                        title: 'Something went wrong',
                        text: 'Please contact us at friends@stanwith.me',
                    });
                },
            }
        );
    };

    const handleOpenDeleteModal = (automationId: number) => {
        setDeleteAutomationId(automationId);
        openDeleteModal();
    };

    const handleCloseDeleteModal = () => {
        closeDeleteModal();

        // Wait before clearing automationId, for modal animation to finish
        setTimeout(() => {
            setDeleteAutomationId(null);
        }, 300);
    };

    const confirmDelete = async () => {
        if (deleteAutomationId) {
            const automationId = deleteAutomationId;

            handleCloseDeleteModal();

            // Delete the automation
            deleteAutomationMutation.mutate(automationId, {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Deleted',
                        text: 'Your auto-DM has been permanently deleted.',
                    });
                },
                onError: () => {
                    stanNotify({
                        type: 'error',
                        title: 'Something went wrong',
                        text: 'Please contact us at friends@stanwith.me',
                    });
                },
            });
        }
    };

    return {
        handlePublishAutomation,
        handleUnpublishAutomation,
        openDeleteModal: handleOpenDeleteModal,
        closeDeleteModal: handleCloseDeleteModal,
        confirmDelete,
        deleteModalState: {
            isOpen: isDeleteModalOpen,
        },
        isPublishing: publishAutomationMutation.isPending,
        isUnpublishing: unpublishAutomationMutation.isPending,
        isDeleting: deleteAutomationMutation.isPending,
    };
}
